import type { DeviceTier } from "../scenario/types";

/**
 * Pick a render tier for THIS device so thousands of varied learner machines
 * each pull the right payload. High = WebGPU + 2M splats; mid = WebGL2 + 500k;
 * low = baked backdrop. The interactive DOM + voice are identical on all tiers.
 */
export interface Capability {
  tier: DeviceTier;
  webgpu: boolean;
  webgl2: boolean;
  deviceMemoryGb: number | null;
  cores: number | null;
  /** Largest GL texture the GPU accepts; tiny values flag weak mobile GPUs. */
  maxTextureSize: number | null;
  /** OS-level "reduce motion" preference (accessibility). */
  reducedMotion: boolean;
  reason: string;
}

const OVERRIDE_KEY = "sim3d:tierOverride";
const MOTION_OVERRIDE_KEY = "sim3d:reducedMotionOverride";
const TIER_ORDER: DeviceTier[] = ["low", "mid", "high"];

/** Next lower tier (used by the runtime FPS watchdog to auto-demote). */
export function nextLowerTier(tier: DeviceTier): DeviceTier {
  const i = TIER_ORDER.indexOf(tier);
  return i > 0 ? TIER_ORDER[i - 1] : "low";
}

/** Manual quality override (persisted) for testing / accessibility / locked GPUs. */
export function setQualityOverride(tier: DeviceTier | null): void {
  try {
    if (tier) localStorage.setItem(OVERRIDE_KEY, tier);
    else localStorage.removeItem(OVERRIDE_KEY);
  } catch {
    /* storage may be blocked; override simply won't persist */
  }
}

/** Current persisted/URL quality override, if any (for the Start-screen UI to
 *  show which preset is active). */
export function getQualityOverride(): DeviceTier | null {
  return readOverride();
}

function readOverride(): DeviceTier | null {
  // URL wins (handy for QA: ?tier=mid), then persisted localStorage value.
  try {
    const url = new URLSearchParams(window.location.search).get("tier");
    if (url && TIER_ORDER.includes(url as DeviceTier)) return url as DeviceTier;
    const stored = localStorage.getItem(OVERRIDE_KEY);
    if (stored && TIER_ORDER.includes(stored as DeviceTier)) return stored as DeviceTier;
  } catch {
    /* ignore */
  }
  return null;
}

/** Manual "reduced motion" override (persisted), independent of the OS-level
 *  `prefers-reduced-motion` media query — lets a learner opt in from the
 *  Start screen even when their OS doesn't advertise the preference (or opt
 *  out, on a device that always advertises it). `null` = follow the OS. */
export function setReducedMotionOverride(v: boolean | null): void {
  try {
    if (v === null) localStorage.removeItem(MOTION_OVERRIDE_KEY);
    else localStorage.setItem(MOTION_OVERRIDE_KEY, v ? "1" : "0");
  } catch {
    /* storage may be blocked; override simply won't persist */
  }
}

/** Current persisted "reduced motion" override, if any (Start-screen UI). */
export function getReducedMotionOverride(): boolean | null {
  return readMotionOverride();
}

function readMotionOverride(): boolean | null {
  try {
    const stored = localStorage.getItem(MOTION_OVERRIDE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    /* ignore */
  }
  return null;
}

export async function probeCapability(): Promise<Capability> {
  const nav = navigator as Navigator & { deviceMemory?: number; gpu?: unknown };
  const deviceMemoryGb = typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
  const cores = typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null;

  let webgpu = false;
  try {
    const gpu = (nav as unknown as { gpu?: { requestAdapter(): Promise<unknown> } }).gpu;
    if (gpu) {
      const adapter = await gpu.requestAdapter();
      webgpu = !!adapter;
    }
  } catch {
    webgpu = false;
  }

  let maxTextureSize: number | null = null;
  const webgl2 = (() => {
    try {
      const c = document.createElement("canvas");
      const gl = c.getContext("webgl2");
      if (!gl) return false;
      maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
      return true;
    } catch {
      return false;
    }
  })();

  const reducedMotion = (() => {
    const manual = readMotionOverride();
    if (manual !== null) return manual;
    try {
      return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch {
      return false;
    }
  })();

  const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
  const lowMemory = deviceMemoryGb !== null && deviceMemoryGb <= 4;
  const fewCores = cores !== null && cores <= 4;
  const weakGpu = maxTextureSize !== null && maxTextureSize < 8192;

  let tier: DeviceTier;
  let reason: string;
  if (webgpu && !isMobile && !lowMemory && !weakGpu) {
    tier = "high";
    reason = "WebGPU + desktop-class GPU/memory";
  } else if (webgl2 && !lowMemory && !fewCores && !isMobile && !weakGpu) {
    tier = "mid";
    reason = "WebGL2, mid-class device";
  } else {
    tier = "low";
    reason = isMobile ? "mobile" : lowMemory ? "low memory" : weakGpu ? "weak GPU" : "no WebGL2";
  }

  const override = readOverride();
  if (override) {
    tier = override;
    reason = `manual override (${override})`;
  }

  return { tier, webgpu, webgl2, deviceMemoryGb, cores, maxTextureSize, reducedMotion, reason };
}
